import type { FinancialYear } from "../types.js";
import {
  type IncomeComponents,
  IncomeComponentsSchema,
  incomeForSurchargePurposes,
  incomeTestsRulesFor,
} from "../income-tests/index.js";
import type { Au08Input } from "./types.js";

/**
 * AU-08 income derivation layer (Item 1 follow-on).
 *
 * The shipped `calculateAu08` takes `input.income` / `input.partnerIncome` as
 * ALREADY-DERIVED "income for MLS purposes" numbers. MLS income IS exactly the
 * ATO "income for surcharge purposes" definition (taxable income + reportable
 * super + net investment losses + net FTDT + the FULL grossed-up RFBA). Rather
 * than re-implement those add-backs here, we route raw components through the
 * shared `income-tests` engine so the engine is the single source of income
 * truth and the 0.53 RFBA exempt-only fix can never drift between products.
 *
 * This is a NON-SHIPPED integration helper — it imports income-tests and feeds
 * the shipped MLS engine; it does NOT mutate `fy2025-26.ts` (architecture rule
 * #1: shipped rulesets are immutable). Pure: components + assessment year in,
 * derived income out.
 */

/** Raw MLS income components for one party (self or partner). */
export interface Au08IncomeComponents {
  /** The party's income components for the income-tests engine. */
  components: IncomeComponents;
}

/**
 * Derive "income for MLS purposes" from raw components via income-tests. The
 * assessment year selects the income-tests ruleset (FBT rate in force) — though
 * for the surcharge definition no FY-varying factor actually applies (the full
 * grossed-up RFBA is used regardless), the year is threaded for consistency and
 * to fail loudly if an unregistered FY is requested.
 */
export function deriveAu08Income(
  components: IncomeComponents,
  assessmentYear: FinancialYear,
): number {
  // Resolve (and thereby validate) the income-tests ruleset for the FY. The
  // surcharge derivation itself does not read any FY-varying factor, but
  // resolving here keeps the "unknown FY fails loudly" contract uniform.
  incomeTestsRulesFor(assessmentYear);
  const c = IncomeComponentsSchema.parse(components);
  return incomeForSurchargePurposes(c);
}

/**
 * Build a `calculateAu08` input from raw self/partner components, deriving the
 * MLS income figures via income-tests. Everything else on the AU-08 input
 * (age, cover flags, filing status, children, firstHeldYear) is passed through
 * unchanged. The result is fed to the shipped `calculateAu08` exactly as a
 * hand-derived income would be — so the engine and the worked examples must
 * reconcile (see derive-income.test.ts).
 */
export function buildAu08InputFromComponents(
  args: {
    self: IncomeComponents;
    partner?: IncomeComponents;
    assessmentYear: FinancialYear;
  } & Omit<Au08Input, "income" | "partnerIncome">,
): Au08Input {
  const { self, partner, assessmentYear, ...rest } = args;
  return {
    ...rest,
    income: deriveAu08Income(self, assessmentYear),
    partnerIncome:
      partner === undefined ? 0 : deriveAu08Income(partner, assessmentYear),
  };
}
